/**
 * **变参行实例名的就地改名** —— 从 `components/NpNode.vue` 向下拆出来的交互块（约 89 行）。
 *
 * ## 和 `useNodeRename` 是两件事（不是一个「改名模块」）
 *
 * 量下来两组**声明的名字交集为 0、各自 emit 不同的事件**
 * （`rename` vs `row-rename`）—— 所以拆成两个文件。
 * 合成一个「改名模块」等于把两件本来不相干的事重新绑在一起。
 *
 * ## ⚠️ 这块里有一条**踩过坑才写对**的规矩，别改回去
 *
 * **输入框必须用函数 ref（`setRowRenameEl`），不能用 `ref="rowRenameEl"`。**
 *
 * 它在一个 `v-for` 里，Vue 会把**字符串 ref** 收集成**数组** ——
 * 于是 `rowRenameEl.value` 成了 `[input]`，`.focus()` 是 undefined：
 * 「点开编辑框没有焦点」；而没有焦点就**不会有 blur**，
 * 「点别处不消失」是同一个原因的两个症状。
 *
 * 同理 `startRowRename` 里**等两帧**：函数 ref 是在 patch 之后才被调用的，
 * 一帧内有时候拿不到元素（列表重排时更明显）。
 *
 * ## 归属
 *
 * **向下**：`NpNode.vue` → `composables/useRowNaming.ts`，只有前者引用它。
 */
import { nextTick, ref } from 'vue'
import type { Ref } from 'vue'
import type { NodeRow } from '@/types/node'

/** 行上的渲染项（普通行没有 `index`，所以是可选的） */
type RowItem = { row: NodeRow; index?: number }

export interface RowNamingCtx {
  /** 抛出去（组件那边接成 `row-rename`） */
  renameRow: (rowId: string, index: number, value: string) => void
  /** 渲染项在变参行里的序号（普通行没有 index，统一从这里取） */
  rowIndex: (item: unknown) => number
}

export interface RowNamingApi {
  /** 正在改的那个实例；`null` = 没在改 */
  renamingRow: Ref<{ rowId: string; index: number } | null>
  setRowRenameEl: (el: unknown) => void
  startRowRename: (item: RowItem) => void
  cancelRowRename: (item: RowItem) => void
  /** 旧的提交式改名：已改成 @input 实时发，留着避免别处引用报错 */
  commitRowRename: (rowId: string, index: number, value: string) => void
}

export function useRowNaming(ctx: RowNamingCtx): RowNamingApi {
  const renamingRow = ref<{ rowId: string; index: number } | null>(null)

  /**
   * 当前正在编辑的那个输入框。
   *
   * ⚠️ **必须用函数 ref**，不能用 `ref="rowRenameEl"`（理由见文件头）。
   */
  const rowRenameEl = ref<HTMLInputElement | null>(null)

  function setRowRenameEl(el: unknown) {
    rowRenameEl.value = (el as HTMLInputElement | null) ?? null
  }

  function startRowRename(item: RowItem) {
    if (item.row.kind !== 'repeater' || item.index === undefined) return
    renamingRow.value = { rowId: item.row.id, index: item.index }
    /*
     * 等两帧：函数 ref 是在 patch 之后才被调用的，
     * 一帧内有时候拿不到元素（列表重排时更明显）。
     */
    void nextTick(() => {
      void nextTick(() => {
        rowRenameEl.value?.focus()
        rowRenameEl.value?.select()
      })
    })
  }

  function cancelRowRename(item: RowItem) {
    if (item.row.kind !== 'repeater') return
    renamingRow.value = null
    ctx.renameRow(item.row.id, ctx.rowIndex(item), '')
  }

  /** 旧的提交式改名：已改成 @input 实时发，保留函数避免别处引用报错 */
  function commitRowRename(rowId: string, index: number, value: string) {
    if (!renamingRow.value) return
    renamingRow.value = null
    ctx.renameRow(rowId, index, value.trim())
  }

  return { renamingRow, setRowRenameEl, startRowRename, cancelRowRename, commitRowRename }
}
